import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { getBlog } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    getBlog().then(setPosts).catch(() => setPosts([]));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="journal-page" className="pt-24">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <p className="overline text-[#B7926A] mb-6">The Journal</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#47394E] leading-[0.95] tracking-tight max-w-3xl">
          <MaskedLines lines={["Notes on clarity,", "connection & bookings."]} />
        </h1>
      </section>

      {featured && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
          <Reveal>
            <button
              onClick={() => setActive(featured)}
              data-testid="journal-featured"
              className="group grid lg:grid-cols-2 gap-8 items-stretch text-left w-full"
            >
              <div className="relative h-[360px] lg:h-[480px] overflow-hidden">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="overline text-[#B7926A]">{featured.category} · {featured.read_time}</span>
                <h2 className="font-display text-4xl lg:text-5xl text-[#47394E] mt-4 leading-tight group-hover:text-[#B7926A] transition-colors">
                  {featured.title}
                </h2>
                <p className="font-body text-lg text-[#47394E]/70 mt-5 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 font-body text-sm text-[#9C8199]">Read the article →</span>
              </div>
            </button>
          </Reveal>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        <div className="grid md:grid-cols-3 gap-10">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <button
                onClick={() => setActive(p)}
                data-testid={`journal-card-${p.slug}`}
                className="group text-left w-full"
              >
                <div className="relative h-[280px] overflow-hidden mb-5">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <span className="overline text-[#B7926A]">{p.category} · {p.read_time}</span>
                <h3 className="font-display text-2xl text-[#47394E] mt-3 group-hover:text-[#B7926A] transition-colors">{p.title}</h3>
                <p className="font-body text-[#47394E]/70 mt-3">{p.excerpt}</p>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-white border-[#EAE3D8] max-h-[85vh] overflow-y-auto" data-testid="journal-dialog">
          {active && (
            <>
              <div className="relative h-56 -mx-6 -mt-6 mb-2 overflow-hidden">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
              </div>
              <DialogHeader>
                <span className="overline text-[#B7926A]">{active.category} · {active.read_time}</span>
                <DialogTitle className="font-display text-3xl lg:text-4xl text-[#47394E] text-left leading-tight">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <div className="font-body text-[#47394E]/80 leading-relaxed mt-2 space-y-4">
                {active.body.split("\n\n").map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              {active.cta_link ? (
                <div className="mt-8 pt-6 border-t border-[#EAE3D8]">
                  {active.cta_text ? <p className="font-body text-[#47394E] mb-4">{active.cta_text}</p> : null}
                  <Link
                    to={active.cta_link}
                    onClick={() => setActive(null)}
                    data-testid="journal-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-[#47394E] text-[#E8DCCF] px-7 py-3.5 font-medium hover:bg-[#B7926A] transition-colors"
                  >
                    {active.cta_label || "Learn more"}
                  </Link>
                </div>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
